import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import VideoPlayer from 'react-video-player-extended';
import { ControlSelection } from 'react-video-player-extended';
import Clipboard from 'react-clipboard.js';
import moment from 'moment-timezone';
import { Order, getOrderDate } from '../models/order';

export interface OrderVideoViewProps {
  order: Order | null;
  selectedStationId: string | null;
  videoTime: string | null;
}

const getStationIndexByStationID = (id: string | null, order: Order | null) => {
  let requiredIndex = 0;

  if (order && order.stationVideos) {
    requiredIndex = order.stationVideos.findIndex(
      (stationVideo) => stationVideo.stationId === id,
    );
  }

  return requiredIndex > -1 ? requiredIndex : 0;
};

const formatDate = (date: any) => date.format('ddd, MMM D, h:mm:ss A');

const StyledContainer = styled.div`
  font-family: Helvetica, sans-serif;
  color: #6e757c;

  .react-video-player {
    max-height: calc(100vh - 250px);
    width: 100%;
  }

  .react-video-player video {
    outline: none;
    max-height: 100%;
  }

  .stations {
    margin: 0px 20px;
    padding: 5px 0px 5px;
    font-size: 12px;
  }

  .order-meta {
    position: relative;
    margin: 0px 20px;
    padding: 0 0 20px 0;
    border-bottom: 1px solid #eee;

    .order-id {
      font-weight: bold;
      font-size: 12px;
    }

    .order-timestamp {
      position: absolute;
      right: 20px;
      top: 5px;
      color: #325882;
      font-size: 12px;
    }
  }

  .reset-time-to-order {
    margin: 0px 0px 0px 5px;
  }
`;

export default function OrderVideoView({
  order,
  selectedStationId,
  videoTime,
}: OrderVideoViewProps) {
  const [playing, setPlaying] = useState(false);
  const [stationIndex, setStationIndex] = useState(
    getStationIndexByStationID(selectedStationId, order),
  );
  const [timeStart, setTimeStart] = useState(videoTime ? Number(videoTime) : 0);
  const [showTimeToOrderReset, setShowTimeToOrderReset] = useState(
    videoTime ? true : false,
  );
  const [timeStartReset, setTimeStartReset] = useState(0);
  const [timeStartStore, setTimeStartStore] = useState(0);
  const [clipboardBgColor, setClipboardBgColor] = useState('transparent');
  const [clipboardSize, setClipboardSize] = useState(1);
  const [frameTimestamps, setFrameTimestamps] = useState<null | Array<number[][]>>(
    null,
  );
  const orderDate = order ? getOrderDate(order) : null;
  const [displayedTimestamp, setDisplayedTimestamp] = useState(
    orderDate ? formatDate(orderDate) : '',
  );

  async function fetchFrameTimestamps() {
    setFrameTimestamps(null);
    if (!order) return null;

    try {
      const frameResponses = await Promise.all(
        order.stationVideos.map((stationVideo) =>
          fetch(stationVideo.videoUrl + '.frames'),
        ),
      );
      const frameMaps = await Promise.all(
        frameResponses.map((frameResponse) => frameResponse.text()),
      );
      const frameMapsIndexes = frameMaps.map((frameMap) => frameMap.split('\n'));
      const frameTimestamps = new Array<number[][]>(frameMapsIndexes.length);

      frameMapsIndexes.forEach((frameMap, stationIndex) => {
        frameTimestamps[stationIndex] = [];
        frameMap.forEach((frame, frameIndex) => {
          const frameSplit = frame.split('->').map((time) => +time);
          frameTimestamps[stationIndex][frameIndex] = [
            frameSplit[0],
            frameSplit[1],
          ];
        });
      });
      setFrameTimestamps(frameTimestamps);

      return frameTimestamps;
    } catch (error) {
      console.log('Error fetching frame timestamps: ', error);
    }

    return null;
  }

  useEffect(() => {
    async function fetchFrameTimestampsAndLoadStart() {
      const fetchedTimestamps = await fetchFrameTimestamps();
      if (!fetchedTimestamps || !order) return;

      let toTimeSeconds = 0;
      if (!videoTime && orderDate) {
        toTimeSeconds = Number(videoTime);
        try {
          const toFrameIndex = binarySearch(
            fetchedTimestamps[stationIndex],
            1,
            orderDate.unix() * 1000,
            0,
            fetchedTimestamps[stationIndex].length - 1,
          );
          toTimeSeconds =
            fetchedTimestamps[stationIndex][toFrameIndex][0] / 1000;
          setDisplayedTimestamp(formatDate(orderDate));
        } catch (error) {
          console.log('Could not jump to order start: ', error);
        }
      }
      setTimeStart(toTimeSeconds);
    }

    fetchFrameTimestampsAndLoadStart();
  }, [order]);

  if (!order) {
    return null;
  }

  const markers = (() => {
    const { recognitionData } = order.stationVideos[stationIndex];
    if (!recognitionData) {
      return [];
    }

    const keyTimes = recognitionData.reduce((keyTimes, data) => {
      keyTimes.push(data.startTime);
      keyTimes.concat(data.keyframesTime || []);
      if (data.endTime) {
        keyTimes.push(data.endTime);
      }
      return keyTimes;
    }, [] as string[]);

    return keyTimes.map((keyTime, index) => ({
      id: index,
      time: keyTime.split(':').reduce((acc, time) => 60 * acc + +time, 0),
      title: '',
    }));
  })();

  const controls: ControlSelection[] = [
    'Play',
    'Time',
    'Progress',
    'Volume',
    'FullScreen',
  ];

  if (markers.length > 0) {
    controls.push('LastFrame');
    controls.push('NextFrame');
  }

  const handlePause = () => {
    setPlaying(false);
  };

  const handlePlay = () => {
    setPlaying(true);
  };

  let progressChange = 0;
  const handleProgress = async (e: any) => {
    if (Math.abs(e.target.currentTime - progressChange) > .25) {
      const stationTime = await getStationTime(
        stationIndex,
        e.target.currentTime,
      );
      if (stationTime !== null && orderDate !== null) {
        const stationTimeDate = moment(stationTime).utcOffset(
          order.location.timezoneOffset * 60,
        );
        setDisplayedTimestamp(formatDate(stationTimeDate));

        const timeDiff = Math.abs(
          stationTimeDate.diff(orderDate, 'milliseconds', true),
        );
        setShowTimeToOrderReset(timeDiff > 3000);
      }

      progressChange = e.target.currentTime;
      setTimeStartStore(e.target.currentTime);
    }
  };

  const onResetTimeToOrder = () => {
    if (frameTimestamps === null || orderDate === null) return;

    const toFrameIndex = binarySearch(
      frameTimestamps[stationIndex],
      1,
      orderDate.unix() * 1000,
      0,
      frameTimestamps[stationIndex].length - 1,
    );
    const toTimeSeconds =
      frameTimestamps[stationIndex][toFrameIndex][0] / 1000;

    const videoElement = document.querySelector(
      '.react-video-player',
    ) as HTMLVideoElement;
    if (videoElement) {
      videoElement.currentTime = toTimeSeconds;
    }
  };

  const handleDuration = (duration: any) => {
    if (timeStartReset !== timeStart) {
      setTimeStart(timeStartReset);
    }
  };

  const binarySearch = (
    arr: number[][],
    searchIndex: number,
    x: number,
    start: number,
    end: number,
  ): any => {
    if (start > end) return false;
    let mid = Math.floor((start + end) / 2);

    if (arr[mid][searchIndex] <= x && x < arr[mid + 1][searchIndex]) return mid;

    if (arr[mid][searchIndex] > x)
      return binarySearch(arr, searchIndex, x, start, mid - 1);
    else return binarySearch(arr, searchIndex, x, mid + 1, end);
  };

  const getStationTime = async (
    stationIndex: number,
    timeSeconds: number,
  ): Promise<number | null> => {
    if (frameTimestamps === null) return null;

    let stationTimeSeconds = null;
    try {
      const frameIndex = binarySearch(
        frameTimestamps[stationIndex],
        0,
        Math.floor(timeSeconds * 1000),
        0,
        frameTimestamps[stationIndex].length - 1,
      );
      stationTimeSeconds = frameTimestamps[stationIndex][frameIndex][1];
    } catch (error) {
      console.log('Error getting station time: ', error);
    }

    return stationTimeSeconds;
  };

  const convertStationTime = (
    fromIndex: number,
    fromTimeSeconds: number,
    toIndex: number,
  ): number => {
    if (frameTimestamps === null) return fromTimeSeconds;

    let toTimeSeconds = fromTimeSeconds;
    try {
      const fromFrameIndex = binarySearch(
        frameTimestamps[fromIndex],
        0,
        Math.floor(fromTimeSeconds * 1000),
        0,
        frameTimestamps[fromIndex].length - 1,
      );
      const fromTimestamp = frameTimestamps[fromIndex][fromFrameIndex][1];
      const toFrameIndex = binarySearch(
        frameTimestamps[toIndex],
        1,
        fromTimestamp,
        0,
        frameTimestamps[toIndex].length - 1,
      );
      toTimeSeconds = frameTimestamps[toIndex][toFrameIndex][0] / 1000;
    } catch (error) {
      console.log('Error converting station time: ', error);
    }

    return toTimeSeconds;
  };

  const onStationClick = (index: number) => {
    setStationIndex(index);
    setTimeStartReset(convertStationTime(stationIndex, timeStartStore, index));
  };

  const getStationBackgroundColor = (
    stationIndex: number,
    index: number,
    currenStationId: string,
  ) => {
    if (stationIndex === index) {
      return { backgroundColor: '#82b1fa' };
    } else {
      return { backgroundColor: '' };
    }
  };

  return (
    <StyledContainer>
      {order.stationVideos[stationIndex].state === 'READY' ? (
        <VideoPlayer
          key={
            order.stationVideos[stationIndex].videoUrl +
            (frameTimestamps
              ? '-' + frameTimestamps[stationIndex][0][1]
              : '-framesloading')
          }
          url={order.stationVideos[stationIndex].videoUrl}
          width="100%"
          height="auto"
          onPlay={handlePlay}
          onPause={handlePause}
          onProgress={handleProgress}
          onDuration={handleDuration}
          isPlaying={playing}
          skipToMarkers={true}
          controls={controls}
          timeStart={timeStart}
          volume={0}
          markers={markers}
        />
      ) : (
        <img
          src={
            order.stationVideos[stationIndex].state === 'PROCESSING'
              ? '/video-processing.svg'
              : '/video-not-found.svg'
          }
          width="100%"
          alt="video placeholder"
        />
      )}
      <div className="stations">
        {order.stationVideos.length > 1 ? (
          <div>
            station:{' '}
            {order.stationVideos.map((video, index) => {
              return (
                <button
                  style={getStationBackgroundColor(
                    stationIndex,
                    index,
                    order.stationVideos[index].stationId,
                  )}
                  onClick={() => onStationClick(index)}
                  key={index}
                  title={order.stationVideos[index].stationId}
                >
                  {order.stationVideos[index].stationName}
                </button>
              );
            })}
          </div>
        ) : (
          ''
        )}
        <div
          style={{
            float: 'right',
          }}
        >
          <Clipboard
            style={{
              background: clipboardBgColor,
              border: 'none',
              cursor: 'pointer',
              transform: `scale(${clipboardSize})`,
            }}
            onClick={() => {
              navigator.clipboard.writeText(
                window.location.origin +
                  '/order-view/' +
                  order.id +
                  '/station/' +
                  order.stationVideos[stationIndex].stationId +
                  '/vt/' +
                  timeStartStore,
              );
              setClipboardBgColor('#f6e19b');
              setTimeout(() => {
                setClipboardBgColor('transparent');
              }, 100);
              setClipboardSize(1.5);
              setTimeout(() => {
                setClipboardSize(1);
              }, 100);
            }}
            button-title="Copy Order Link"
          >
            🔗
          </Clipboard>
        </div>
      </div>
      <div className="order-meta">
        <p className="order-id">{order.platformId}</p>
        <p className="order-timestamp">
          {displayedTimestamp}
          {showTimeToOrderReset ? (
            <button
              className="reset-time-to-order"
              onClick={onResetTimeToOrder}
              title="Reset time to order"
            >
              ↺
            </button>
          ) : (
            ''
          )}
        </p>
      </div>
    </StyledContainer>
  );
}

